import { useEffect, type RefObject } from 'react';

const SCROLL_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' '
]);

/**
 * Locks page scroll when active. Prevents both CSS overflow and keyboard-triggered scrolling.
 * Scroll keys still work inside the provided container ref (e.g. a scrollable drawer/modal body).
 */
export function useScrollLock(active: boolean, containerRef?: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!active) return;

    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!SCROLL_KEYS.has(e.key)) return;

      const target = e.target as HTMLElement;

      // Allow scroll keys inside interactive elements (inputs, textareas, selects)
      const tagName = target.tagName;
      if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') return;

      // Allow scroll keys inside the overlay container (so its own content can scroll)
      if (containerRef?.current?.contains(target)) return;

      e.preventDefault();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [active, containerRef]);
}
